import api from './api';
import dayjs from 'dayjs';

export interface CskhDanhMucDto {
  ma: number;
  ten: string;
}

export interface CskhBenhNhanDto {
  maKCB: string;
  maBN: string;
  hoTen: string;
  namSinh?: number | null;
  tuoi?: number | null;
  tenPhai?: string | null;
  diaChi?: string | null;
  ngayDK?: string | null;
  tenDangDieuTri?: string | null;
  tenDoiTuong?: string | null;
  tenKK?: string | null;
  tenPhong?: string | null;
  dienThoai?: string | null;
  soCMND?: string | null;
  ngayCapCMND?: string | null;
  soBHXH?: string | null;
  chanDoanChinh?: string | null;
  chanDoanKemTheo?: string | null;
}

// ===== Phiếu CSKH =====
export interface CskhDmCachLienHeDto {
  maCLH: number;
  tenCLH: string;
}
export interface CskhDmLoaiDanhGiaDto {
  maLDG: number;
  tenLoaiDG: string;
}
export interface CskhDmTrangThaiDto {
  maTT: number;
  tenTT: string;
}
export interface CskhPhieuDto {
  idPhieu: number;
  maKCB: string;
  maBN: string;
  maNV: string;
  tenNV?: string | null;
  maCLH: number;
  tenCLH?: string | null;
  maTT: number;
  tenTT?: string | null;
  maLDG?: number | null;
  tenLoaiDG?: string | null;
  ngayGoi: string;
  ghiChu?: string | null;
}
export interface CskhPhieuCreateRequest {
  maKCB: string;
  maBN: string;
  maCLH: number;
  maTT: number;
  maLDG?: number | null;
  ngayGoi: string;
  ghiChu?: string | null;
}
export interface CskhPhieuSummaryDto {
  maBN: string;
  soPhieu: number;
  ngayGoiGanNhat?: string | null;
}

export interface CskhDichVuDto {
  maView: string;
  tenDichVu: string;
  giaBHYT?: number | null;
  giaVienPhi?: number | null;
}

export interface CskhDichVuSuDungDto {
  maTTCT: number;
  maLoaiDichVu: number;
  tenLoaiDichVu: string;
  maView: string;
  tenDichVu: string;
  soLuong: number | null;
  thanhTien: number | null;
  thanhTienBH: number | null;
  tienBNCCT: number | null;
  tienBN: number | null;
}

export interface CskhReportDto {
  idPhieu: number;
  maKCB: string;
  maBN: string;
  hoTen: string;
  dienThoai?: string | null;
  maNV: string;
  tenNV?: string | null;
  tenCLH?: string | null;
  tenTT?: string | null;
  maTT?: number;
  tenLoaiDG?: string | null;
  ngayGoi: string;
  ghiChu?: string | null;
  tenKK?: string | null;
}

export const cskhService = {
  getDangDieuTri: async (): Promise<CskhDanhMucDto[]> => {
    const response = await api.get('/Cskh/dang-dieu-tri');
    return response.data || [];
  },

  getDoiTuong: async (): Promise<CskhDanhMucDto[]> => {
    const response = await api.get('/Cskh/doi-tuong');
    return response.data || [];
  },

  getKhoaKham: async (): Promise<CskhDanhMucDto[]> => {
    const response = await api.get('/Cskh/khoa-kham');
    return response.data || [];
  },

  // MỚI: Lấy phòng theo khoa (Cascaded dropdown)
  getPhongByKhoa: async (maKK: number): Promise<CskhDanhMucDto[]> => {
    const response = await api.get('/Cskh/phong-theo-khoa', { params: { maKK } });
    return response.data || [];
  },

  getBenhNhan: async (params: {
    tuNgay: dayjs.Dayjs;
    denNgay: dayjs.Dayjs;
    maDangDieuTri: number;
    maDoiTuong: number;
    maKK: number;
    maphong: number;
    searchTerm?: string;
  }): Promise<CskhBenhNhanDto[]> => {
    const response = await api.get('/Cskh/benh-nhan', {
      params: {
        tuNgay: params.tuNgay.format('YYYY-MM-DD'),
        denNgay: params.denNgay.format('YYYY-MM-DD'),
        maDangDieuTri: params.maDangDieuTri,
        maDoiTuong: params.maDoiTuong,
        maKK: params.maKK,
        maphong: params.maphong,
        searchTerm: params.searchTerm
      }
    });
    return response.data || [];
  },

  getBenhNhanChiTiet: async (maKCB: string): Promise<CskhBenhNhanDto | null> => {
    const response = await api.get('/Cskh/benh-nhan-chi-tiet', {
      params: { maKCB }
    });
    return response.data || null;
  },

  getDmDichVu: async (searchTerm?: string): Promise<CskhDichVuDto[]> => {
    const response = await api.get('/Cskh/dm-dich-vu', {
      params: { searchTerm }
    });
    return response.data || [];
  },

  getReport: async (params: {
    tuNgay: dayjs.Dayjs;
    denNgay: dayjs.Dayjs;
    maNV?: string | null;
    maTT?: number;
  }): Promise<CskhReportDto[]> => {
    const response = await api.get('/Cskh/report', {
      params: {
        tuNgay: params.tuNgay.format('YYYY-MM-DD'),
        denNgay: params.denNgay.format('YYYY-MM-DD'),
        maNV: params.maNV,
        maTT: params.maTT
      }
    });
    return response.data || [];
  },

  // ===== Phiếu CSKH =====
  getDmCachLienHe: async (): Promise<CskhDmCachLienHeDto[]> => {
    const res = await api.get('/Cskh/dm-cach-lien-he');
    return res.data || [];
  },
  getDmLoaiDanhGia: async (): Promise<CskhDmLoaiDanhGiaDto[]> => {
    const res = await api.get('/Cskh/dm-loai-danh-gia');
    return res.data || [];
  },
  getDmTrangThai: async (): Promise<CskhDmTrangThaiDto[]> => {
    const res = await api.get('/Cskh/dm-trang-thai');
    return res.data || [];
  },
  createPhieu: async (payload: CskhPhieuCreateRequest): Promise<{ idPhieu: number; message: string }> => {
    const res = await api.post('/Cskh/tao-phieu', payload);
    return res.data;
  },
  getDanhSachPhieu: async (maBN: string): Promise<CskhPhieuDto[]> => {
    const res = await api.get('/Cskh/danh-sach-phieu', { params: { maBN } });
    return res.data || [];
  },
  getSummaryPhieu: async (maBNList: string[]): Promise<CskhPhieuSummaryDto[]> => {
    const res = await api.post('/Cskh/summary-phieu', maBNList);
    return res.data || [];
  },

  getDichVuByMaKCB: async (maKCB: string): Promise<CskhDichVuSuDungDto[]> => {
    const res = await api.get('/Cskh/dich-vu-theo-makcb', { params: { maKCB } });
    return res.data || [];
  }
};
